import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import { getBookingReport, exportBookingReportToExcel } from "../../components/Api/GmsBookingReport";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { Filter, Download, Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const emptyFilter = {
  customer_Id: null,
  employee_Id: null,
  fromDate: null,
  toDate: null,
  month: null,
  year: null,
  is_Cancelled: null,
};

export default function BookingReportPage() {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filter options
  const statusOptions = [
    { value: null, label: "All" },
    { value: false, label: "Active" },
    { value: true, label: "Cancelled" }
  ];

  const monthOptions = [
    { value: null, label: "Select Month" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const yearOptions = [
    { value: null, label: "Select Year" },
    { value: 2024, label: "2024" },
    { value: 2025, label: "2025" },
    { value: 2026, label: "2026" }
  ];

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsBookingReport');

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  useEffect(() => {
    if (customers.length > 0 && employees.length > 0) {
      loadReportData();
    }
  }, [customers, employees]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCustomers(),
        loadEmployees(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({ status: true });
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    console.log('🔍 searchCustomersManually called with inputValue:', inputValue);
    
    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      console.log('🚀 Making API call to searchCustomers with:', { inputValue, page: 1, pageSize: 20 });
      const response = await searchCustomers(inputValue, 1, 20);
      console.log('✅ API response received:', response);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({ status: true });
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadReportData = async () => {
    loadReportDataWithFilter(filter);
  };

  const loadReportDataWithFilter = async (filterToUse) => {
    try {
      setLoading(true);
      const filterData = {
        customer_Id: filterToUse.customer_Id,
        employee_Id: filterToUse.employee_Id,
        fromDate: filterToUse.fromDate,
        toDate: filterToUse.toDate,
        month: filterToUse.month,
        year: filterToUse.year,
        is_Cancelled: filterToUse.is_Cancelled
      };
      
      const data = await getBookingReport(filterData);
      setReportData(data || []);
    } catch (error) {
      console.error("Error loading report data:", error);
      Swal.fire("Error", "Failed to load booking report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    loadReportData();
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    // Load all data with empty filters
    loadReportDataWithFilter(emptyFilter);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const filterData = {
        customer_Id: filter.customer_Id,
        employee_Id: filter.employee_Id,
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        month: filter.month,
        year: filter.year,
        is_Cancelled: filter.is_Cancelled
      };
      
      await exportBookingReportToExcel(filterData);
      Swal.fire("Success", "Booking report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting report:", error);
      Swal.fire("Error", "Failed to export booking report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (field, date) => {
    setFilter(prev => ({
      ...prev,
      [field]: date,
      // Clear month/year when date is selected
      month: null,
      year: null
    }));
  };

  const handleMonthYearChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
      // Clear date range when month/year is selected
      fromDate: null,
      toDate: null
    }));
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (error) {
      return dateString;
    }
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Booking Report" breadcrumbItem="Reports" />
        
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Booking Report</h4>
                  <div>
                    <Button
                      color="primary"
                      className="me-2"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="me-1" size={16} />
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    <Button
                      color="success"
                      onClick={handleExportExcel}
                      disabled={loading || reportData.length === 0}
                    >
                      <Download className="me-1" size={16} />
                      Export Excel
                    </Button>
                  </div>
                </div>

                {showFilters && (
                  <Card className="mb-3">
                    <CardBody>
                      <Row>
                        <Col md="3">
                          <Label>Customer</Label>
                          <Select
                            value={selectedCustomerFilterOption}
                            onChange={(selected) => {
                              setSelectedCustomerFilterOption(selected);
                              setFilter(prev => ({ ...prev, customer_Id: selected?.value || null }));
                            }}
                            options={customerSearchOptions}
                            placeholder="Type at least 3 characters to search customers..."
                            isClearable
                            isSearchable
                            styles={selectStyles}
                            isLoading={customerSearchLoading}
                            noOptionsMessage={() => "Type at least 3 characters to search"}
                            loadingMessage={() => "Searching customers..."}
                            onInputChange={(inputValue) => {
                              console.log('🔤 Input changed:', inputValue);
                              
                              // Clear previous timeout
                              if (searchTimeout) {
                                clearTimeout(searchTimeout);
                              }
                              
                              // Set new timeout
                              const timeout = setTimeout(() => {
                                searchCustomersManually(inputValue);
                              }, 300);
                              setSearchTimeout(timeout);
                              
                              return inputValue;
                            }}
                            onMenuOpen={() => console.log('📂 Menu opened')}
                            onMenuClose={() => console.log('📁 Menu closed')}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Employee</Label>
                          <Select
                            value={employees.find(e => e.employee_Id === filter.employee_Id)}
                            onChange={(selected) => setFilter(prev => ({ ...prev, employee_Id: selected?.employee_Id || null }))}
                            options={employees}
                            getOptionLabel={(option) => option.employee_NameEn}
                            getOptionValue={(option) => option.employee_Id}
                            placeholder="Select Employee"
                            isClearable
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Status</Label>
                          <Select
                            value={statusOptions.find(s => s.value === filter.is_Cancelled)}
                            onChange={(selected) => setFilter(prev => ({ ...prev, is_Cancelled: selected?.value }))}
                            options={statusOptions}
                            placeholder="Select Status"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>From Date</Label>
                          <DatePicker
                            selected={filter.fromDate}
                            onChange={(date) => handleDateChange('fromDate', date)}
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            placeholderText="Select From Date"
                          />
                        </Col>
                      </Row>
                      <Row className="mt-3">
                        <Col md="3">
                          <Label>To Date</Label>
                          <DatePicker
                            selected={filter.toDate}
                            onChange={(date) => handleDateChange('toDate', date)}
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            placeholderText="Select To Date"
                          />
                        </Col>
                        <Col md="3">
                          <Label>Month</Label>
                          <Select
                            value={monthOptions.find(m => m.value === filter.month)}
                            onChange={(selected) => handleMonthYearChange('month', selected?.value)}
                            options={monthOptions}
                            placeholder="Select Month"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3">
                          <Label>Year</Label>
                          <Select
                            value={yearOptions.find(y => y.value === filter.year)}
                            onChange={(selected) => handleMonthYearChange('year', selected?.value)}
                            options={yearOptions}
                            placeholder="Select Year"
                            styles={selectStyles}
                          />
                        </Col>
                        <Col md="3" className="d-flex align-items-end">
                          <div>
                            <Button color="primary" onClick={handleApplyFilter} className="me-2">
                              Apply Filter
                            </Button>
                            <Button color="secondary" onClick={handleClearFilter}>
                              Clear Filter
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                <div className="table-responsive">
                  <Table className="table table-striped table-bordered">
                    <thead>
                      <tr>
                        <th>Serial No.</th>
                        <th>Customer</th>
                        <th>Booking Date</th>
                        <th>Entry Date</th>
                        <th>Services</th>
                        <th>Employees</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            <ClipLoader size={20} color="#556ee6" />
                            <span className="ms-2">Loading...</span>
                          </td>
                        </tr>
                      ) : reportData.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">No data found</td>
                        </tr>
                      ) : (
                        reportData.map((item, index) => (
                          <tr key={index}>
                            <td>{item.serialNumber}</td>
                            <td>{item.customer}</td>
                            <td>{formatDate(item.booking_Date)}</td>
                            <td>{formatDate(item.entry_Date)}</td>
                            <td>{item.services}</td>
                            <td>{item.employees}</td>
                            <td>
                              <span className={`badge ${item.status === 'Active' ? 'bg-success' : 'bg-danger'}`}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
